import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  Button,
  Space,
  Tag,
  Popconfirm,
  message,
  Select,
  Tabs,
  Input,
  Modal,
} from "antd";
import {
  TeamOutlined,
  SwapOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  InboxOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import { useTableSelection } from "../../../../hooks";
import { organizationAPI } from "../../../../api/organizationApi";
import { fetchUsers } from "../../../../api/userManagement";

const UNASSIGNED = "待分配组织";
const isUnassignedName = (name) => name === UNASSIGNED;

const collectDescendantIds = (tree, targetId) => {
  const ids = [];
  const find = (nodes) => {
    for (const node of nodes) {
      if (node.id === targetId) {
        const walk = (n) => {
          if (n.children) {
            n.children.forEach((child) => {
              ids.push(child.id);
              walk(child);
            });
          }
        };
        walk(node);
        return true;
      }
      if (node.children && find(node.children)) return true;
    }
    return false;
  };
  find(tree);
  return ids;
};

const MemberWorkspace = ({
  selectedOrg,
  selectedUserIds,
  onSelectUsers,
  onInspect,
  onReloadTree,
  canUpdate,
  canDelete,
  organizationTree,
  organizationFlatList,
  onOpenBatchTransfer,
}) => {
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("members");
  const [memberSearch, setMemberSearch] = useState("");
  const [unassignedMembers, setUnassignedMembers] = useState([]);
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [singleTransferUser, setSingleTransferUser] = useState(null);
  const [singleTransferTarget, setSingleTransferTarget] = useState(null);

  const loadMembers = useCallback(
    async (orgId) => {
      if (!orgId) {
        setMembers([]);
        return;
      }
      try {
        setMembersLoading(true);
        const descendantIds = collectDescendantIds(organizationTree, orgId);
        const orgIds = [orgId, ...descendantIds];

        const responses = await Promise.all(
          orgIds.map((id) => organizationAPI.getOrganizationUsers(id))
        );

        const memberMap = new Map();
        responses.forEach((res, idx) => {
          const currentOrgId = orgIds[idx];
          const orgName =
            organizationFlatList.find((o) => o.id === currentOrgId)?.name ||
            `ID ${currentOrgId}`;
          (res.items || []).forEach((user) => {
            if (!memberMap.has(user.id)) {
              memberMap.set(user.id, { ...user, organization_name: orgName });
            }
          });
        });

        setMembers(Array.from(memberMap.values()));
      } catch {
        message.error("加载成员失败");
      } finally {
        setMembersLoading(false);
      }
    },
    [organizationTree, organizationFlatList]
  );

  const loadUnassigned = useCallback(async () => {
    const unassignedOrg = organizationFlatList.find((o) =>
      isUnassignedName(o.name)
    );
    if (!unassignedOrg) return;
    try {
      setUnassignedLoading(true);
      const res = await organizationAPI.getOrganizationUsers(unassignedOrg.id);
      setUnassignedMembers(res.items || []);
    } catch {
      message.error("加载待分配用户失败");
    } finally {
      setUnassignedLoading(false);
    }
  }, [organizationFlatList]);

  useEffect(() => {
    if (selectedOrg) {
      loadMembers(selectedOrg.id);
      setActiveTab("members");
      setMemberSearch("");
    } else {
      setMembers([]);
    }
  }, [selectedOrg, loadMembers]);

  useEffect(() => {
    if (activeTab === "unassigned") loadUnassigned();
  }, [activeTab, loadUnassigned]);

  useEffect(() => {
    if (!canUpdate) return;
    let cancelled = false;
    const load = async () => {
      try {
        setUsersLoading(true);
        const res = await fetchUsers({ page: 1, page_size: 500 });
        if (!cancelled) setAllUsers(res.items || []);
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [canUpdate]);

  const memberIdSet = useMemo(
    () => new Set(members.map((m) => m.id)),
    [members]
  );

  const addableOptions = useMemo(() => {
    if (!selectedOrg || isUnassignedName(selectedOrg.name)) return [];
    return allUsers
      .filter((u) => !memberIdSet.has(u.id))
      .map((u) => ({
        value: u.id,
        label: `${u.username} (${u.full_name || u.email || "-"})`,
      }));
  }, [allUsers, memberIdSet, selectedOrg]);

  const transferTargetOptions = useMemo(() => {
    if (!selectedOrg) return [];
    return organizationFlatList
      .filter((o) => o.id !== selectedOrg.id && !isUnassignedName(o.name))
      .map((o) => ({ value: o.id, label: o.pathLabel || o.name }));
  }, [organizationFlatList, selectedOrg]);

  const handleAddMember = async () => {
    if (!selectedOrg?.id || !pendingUserId) return;
    try {
      setMembersLoading(true);
      await organizationAPI.assignUserToOrganization(
        selectedOrg.id,
        pendingUserId
      );
      message.success("已加入组织");
      setPendingUserId(null);
      await loadMembers(selectedOrg.id);
      onReloadTree();
    } catch (err) {
      message.error(err.response?.data?.detail || "加入失败");
    } finally {
      setMembersLoading(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedOrg?.id) return;
    try {
      await organizationAPI.removeUserFromOrganization(
        selectedOrg.id,
        userId
      );
      message.success("已移除");
      await loadMembers(selectedOrg.id);
      onReloadTree();
    } catch (err) {
      message.error(err.response?.data?.detail || "移除失败");
    }
  };

  const handleSingleTransfer = async () => {
    if (!selectedOrg?.id || !singleTransferUser || !singleTransferTarget)
      return;
    try {
      setMembersLoading(true);
      await organizationAPI.assignUserToOrganization(
        singleTransferTarget,
        singleTransferUser.id
      );
      if (!isUnassignedName(selectedOrg.name)) {
        await organizationAPI.removeUserFromOrganization(
          selectedOrg.id,
          singleTransferUser.id
        );
      }
      message.success("已切换组织");
      setSingleTransferUser(null);
      setSingleTransferTarget(null);
      await loadMembers(selectedOrg.id);
      onReloadTree();
    } catch (err) {
      message.error(err.response?.data?.detail || "切换失败");
    } finally {
      setMembersLoading(false);
    }
  };

  const handleTransferUnassigned = async (userId, targetOrgId) => {
    if (!targetOrgId) return;
    try {
      await organizationAPI.assignUserToOrganization(targetOrgId, userId);
      message.success("已转入正式组织");
      await loadUnassigned();
      onReloadTree();
    } catch (err) {
      message.error(err.response?.data?.detail || "转入失败");
    }
  };

  const filteredMembers = useMemo(() => {
    if (!memberSearch.trim()) return members;
    const term = memberSearch.toLowerCase();
    return members.filter(
      (m) =>
        (m.username || "").toLowerCase().includes(term) ||
        (m.full_name || "").toLowerCase().includes(term) ||
        (m.email || "").toLowerCase().includes(term)
    );
  }, [members, memberSearch]);

  // useTableSelection 滑动选择
  const {
    selectedRowKeys,
    rowSelection,
    getIndexColumn,
    getRowProps,
    clearSelection,
  } = useTableSelection({
    rowKey: "id",
    dataSource: filteredMembers,
    pagination: { current: 1, pageSize: filteredMembers.length || 10 },
    onSelectionChange: (keys) => onSelectUsers(keys),
  });

  // 切换组织时清空选择
  useEffect(() => {
    clearSelection();
  }, [selectedOrg?.id, clearSelection]);

  const columns = [
    {
      title: "用户名",
      dataIndex: "username",
      key: "username",
      width: 140,
    },
    {
      title: "姓名",
      dataIndex: "full_name",
      key: "full_name",
      width: 120,
      render: (v) => v || "-",
    },
    {
      title: "邮箱",
      dataIndex: "email",
      key: "email",
      render: (v) => v || "-",
    },
    {
      title: "组织",
      dataIndex: "organization_name",
      key: "organization_name",
      width: 160,
      render: (v) => v || "-",
    },
    {
      title: "状态",
      dataIndex: "is_active",
      key: "is_active",
      width: 80,
      render: (v) => (
        <Tag color={v ? "green" : "default"} size="small">
          {v ? "活跃" : "禁用"}
        </Tag>
      ),
    },
    {
      title: "操作",
      key: "action",
      width: 240,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<TeamOutlined />}
            onClick={() => onInspect("user", record.id)}
          >
            查看归属
          </Button>
          {canUpdate && !isUnassignedName(selectedOrg?.name) && (
            <>
              <Button
                type="link"
                size="small"
                icon={<SwapOutlined />}
                onClick={() => {
                  setSingleTransferUser(record);
                  setSingleTransferTarget(null);
                }}
              >
                换组织
              </Button>
              <Popconfirm
                title={`移除 ${record.username}？`}
                onConfirm={() => handleRemoveMember(record.id)}
              >
                <Button
                  type="link"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                >
                  移除
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #f0f0f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <Space>
          {selectedOrg ? (
            <span style={{ fontWeight: 600, fontSize: 16 }}>
              <ApartmentOutlined style={{ marginRight: 8 }} />
              {selectedOrg.name}
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {members.length} 人
              </Tag>
            </span>
          ) : (
            <span style={{ color: "#999" }}>请从左侧选择一个组织</span>
          )}
        </Space>
        <Space wrap>
          {canUpdate && selectedOrg && !isUnassignedName(selectedOrg.name) && (
            <>
              <Select
                showSearch
                size="small"
                style={{ width: 220 }}
                placeholder="搜索用户加入"
                value={pendingUserId}
                onChange={setPendingUserId}
                options={addableOptions}
                loading={usersLoading}
                allowClear
                optionFilterProp="label"
              />
              {pendingUserId && (
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={handleAddMember}
                >
                  加入
                </Button>
              )}
            </>
          )}
        </Space>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "0 16px" }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "members",
              label: "成员",
              children: selectedOrg ? (
                <>
                  <div style={{ margin: "12px 0" }}>
                    <Input
                      prefix={<SearchOutlined />}
                      placeholder="搜索成员"
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      allowClear
                      size="small"
                      style={{ width: 240 }}
                    />
                  </div>
                  <Table
                    columns={[getIndexColumn("序号", 60), ...columns]}
                    dataSource={filteredMembers}
                    rowKey="id"
                    loading={membersLoading}
                    size="small"
                    pagination={
                      filteredMembers.length > 20 ? { pageSize: 20 } : false
                    }
                    rowSelection={rowSelection}
                    onRow={getRowProps}
                    scroll={{ x: 800 }}
                  />
                </>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: 80,
                    color: "#999",
                  }}
                >
                  <TeamOutlined
                    style={{ fontSize: 48, marginBottom: 16 }}
                  />
                  <div>请在左侧选择一个组织</div>
                </div>
              ),
            },
            {
              key: "unassigned",
              label: (
                <span>
                  <InboxOutlined style={{ marginRight: 4 }} />
                  待分配用户
                  {unassignedMembers.length > 0 && (
                    <Tag
                      color="orange"
                      size="small"
                      style={{ marginLeft: 4 }}
                    >
                      {unassignedMembers.length}
                    </Tag>
                  )}
                </span>
              ),
              children: (
                <Table
                  columns={[
                    {
                      title: "用户名",
                      dataIndex: "username",
                      key: "username",
                    },
                    {
                      title: "姓名",
                      dataIndex: "full_name",
                      key: "full_name",
                      render: (v) => v || "-",
                    },
                    {
                      title: "邮箱",
                      dataIndex: "email",
                      key: "email",
                      render: (v) => v || "-",
                    },
                    {
                      title: "状态",
                      dataIndex: "is_active",
                      key: "is_active",
                      width: 80,
                      render: (v) => (
                        <Tag
                          color={v ? "green" : "default"}
                          size="small"
                        >
                          {v ? "活跃" : "禁用"}
                        </Tag>
                      ),
                    },
                    {
                      title: "操作",
                      key: "action",
                      render: (_, record) => (
                        <Space>
                          <Select
                            size="small"
                            style={{ width: 200 }}
                            placeholder="选择目标组织转入"
                            options={organizationFlatList
                              .filter((o) => !isUnassignedName(o.name))
                              .map((o) => ({
                                value: o.id,
                                label: o.pathLabel || o.name,
                              }))}
                            onChange={(val) =>
                              handleTransferUnassigned(record.id, val)
                            }
                          />
                          <Button
                            type="link"
                            size="small"
                            onClick={() => onInspect("user", record.id)}
                          >
                            查看归属
                          </Button>
                        </Space>
                      ),
                    },
                  ]}
                  dataSource={unassignedMembers}
                  rowKey="id"
                  loading={unassignedLoading}
                  size="small"
                  pagination={
                    unassignedMembers.length > 20 ? { pageSize: 20 } : false
                  }
                />
              ),
            },
          ]}
        />
      </div>

      <Modal
        title={
          singleTransferUser
            ? `换组织 - ${singleTransferUser.username}`
            : "换组织"
        }
        open={!!singleTransferUser}
        onOk={handleSingleTransfer}
        onCancel={() => {
          setSingleTransferUser(null);
          setSingleTransferTarget(null);
        }}
        okButtonProps={{ disabled: !singleTransferTarget }}
      >
        <div style={{ marginBottom: 12 }}>
          当前组织：<strong>{selectedOrg?.name}</strong>
        </div>
        <div>目标组织：</div>
        <Select
          showSearch
          style={{ width: "100%", marginTop: 8 }}
          placeholder="选择目标组织"
          value={singleTransferTarget}
          onChange={setSingleTransferTarget}
          options={transferTargetOptions}
          optionFilterProp="label"
        />
      </Modal>
    </div>
  );
};

export default MemberWorkspace;
